import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and cart token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  const cartToken = localStorage.getItem('cart_token');
  if (cartToken) {
    config.headers['X-Cart-Token'] = cartToken;
  }
  
  return config;
});

// Capture cart token from response headers
api.interceptors.response.use((response) => {
  const cartToken = response.headers['x-cart-token'];
  if (cartToken) {
    localStorage.setItem('cart_token', cartToken);
  }
  return response;
}, (error) => {
  return Promise.reject(error);
});

export const authService = {
  login: async (email, password) => {
    const response = await api.post('/login', { email, password });
    return response.data;
  },
  
  register: async (name, email, password, passwordConfirmation) => {
    const response = await api.post('/register', {
      name,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/logout');
    return response.data;
  },
  
  getUser: async () => {
    const response = await api.get('/me');
    return response.data.user || response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/me', data);
    return response.data;
  },
};

export const searchService = {
  query: async (q, options = {}) => {
    const params = { q, ...options };
    const response = await api.get('/search', { params });
    return response.data;
  },
};


export const productService = {
  index: async (params = {}) => {
    const response = await api.get('/products', { params });
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/products', data);
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data.data || response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/products/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  storeVariant: async (productId, data) => {
    const response = await api.post(`/products/${productId}/variants`, data);
    return response.data;
  },

  updateVariant: async (productId, variantId, data) => {
    const response = await api.put(`/products/${productId}/variants/${variantId}`, data);
    return response.data.data || response.data;
  },

  destroyVariant: async (productId, variantId) => {
    const response = await api.delete(`/products/${productId}/variants/${variantId}`);
    return response.data;
  },

  storeOffer: async (productId, data) => {
    const response = await api.post(`/products/${productId}/offers`, data);
    return response.data;
  },

  storeReview: async (productId, data) => {
    const response = await api.post(`/products/${productId}/reviews`, data);
    return response.data;
  },

  storeTag: async (name) => {
    const response = await api.post('/product-tags', { name });
    return response.data.data || response.data;
  },

  attachTags: async (productId, tagIds) => {
    const response = await api.post(`/products/${productId}/tags`, { tag_ids: tagIds });
    return response.data;
  },

  approveReview: async (id) => {
    const response = await api.post(`/product-reviews/${id}/approve`);
    return response.data;
  },
};

export const categoryService = {
  index: async () => {
    const response = await api.get('/categories');
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/categories', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const pageService = {
  index: async (params = {}) => {
    const response = await api.get('/pages', { params });
    return response.data.data || response.data;
  },
  
  show: async (slug) => {
    const response = await api.get(`/pages/${slug}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/pages', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/pages/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/pages/${id}`);
    return response.data;
  },
};

export const bannerService = {
  index: async (params = {}) => {
    const response = await api.get('/banners', { params });
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/banners', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/banners/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/banners/${id}`);
    return response.data;
  },
};

export const menuService = {
  index: async (params = {}) => {
    const response = await api.get('/menus', { params });
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/menus', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/menus/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/menus/${id}`);
    return response.data;
  },
  
  addItem: async (menuId, data) => {
    const response = await api.post(`/menus/${menuId}/items`, data);
    return response.data;
  },
};

export const orderService = {
  index: async (params = {}) => {
    const response = await api.get('/orders', { params });
    return response.data.data || response.data;
  },
  
  show: async (orderNumber) => {
    const response = await api.get(`/orders/${orderNumber}`);
    return response.data;
  },
  
  checkout: async (data) => {
    const response = await api.post('/checkout', data);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await api.put(`/orders/${id}/status`, { status });
    return response.data;
  },
};

export const cartService = {
  index: async () => {
    const response = await api.get('/cart');
    return response.data.data || response.data;
  },
  
  addItem: async (productId, quantity = 1, variantId = null, metadata = null) => {
    const response = await api.post('/cart/items', {
      product_id: productId,
      product_variant_id: variantId,
      quantity,
      metadata,
    });
    return response.data.data || response.data;
  },
  
  updateItem: async (id, quantity) => {
    const response = await api.put(`/cart/items/${id}`, { quantity });
    return response.data.data || response.data;
  },
  
  removeItem: async (id) => {
    const response = await api.delete(`/cart/items/${id}`);
    return response.data.data || response.data;
  },
  
  clear: async () => {
    const response = await api.delete('/cart');
    return response.data;
  },
};

export const addressService = {
  index: async () => {
    const response = await api.get('/addresses');
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/addresses', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/addresses/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/addresses/${id}`);
    return response.data;
  },
};

export const wishlistService = {
  index: async () => {
    const response = await api.get('/wishlist');
    return response.data.data || response.data;
  },
  
  store: async (productId) => {
    const response = await api.post('/wishlist', { product_id: productId });
    return response.data;
  },
  
  destroy: async (productId) => {
    const response = await api.delete(`/wishlist/${productId}`);
    return response.data;
  },
};

export const couponService = {
  index: async () => {
    const response = await api.get('/coupons');
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/coupons', data);
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/coupons/${id}`);
    return response.data.data || response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/coupons/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/coupons/${id}`);
    return response.data;
  },
  
  validate: async (code, orderAmount) => {
    const response = await api.post('/coupons/validate', {
      code,
      order_amount: orderAmount,
    });
    return response.data;
  },
};

export const invoiceService = {
  index: async () => {
    const response = await api.get('/invoices');
    return response.data.data || response.data;
  },
  
  show: async (invoiceNumber) => {
    const response = await api.get(`/invoices/${invoiceNumber}`);
    return response.data;
  },
};

export const inventoryService = {
  index: async (params = {}) => {
    const response = await api.get('/inventory', { params });
    return response.data.data || response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/inventory/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/inventory', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/inventory/${id}`);
    return response.data;
  },
  
  restock: async (id, quantity) => {
    const response = await api.post(`/inventory/${id}/restock`, { quantity });
    return response.data;
  },
};

export const dealerService = {
  index: async (params = {}) => {
    const response = await api.get('/dealers', { params });
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/dealers/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/dealers', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/dealers/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/dealers/${id}`);
    return response.data;
  },
};

export const distributorService = {
  index: async () => {
    const response = await api.get('/distributors');
    return response.data.data || response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/distributors/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/distributors', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/distributors/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/distributors/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/distributors/${id}/approve`);
    return response.data;
  },
  
  suspend: async (id) => {
    const response = await api.post(`/distributors/${id}/suspend`);
    return response.data;
  },
};

export const investmentService = {
  index: async () => {
    const response = await api.get('/investments');
    // If the backend returns data: { data: [...] } for pagination or collection wrapping
    if (response.data && response.data.data) {
      return response.data.data;
    }
    // If it returns just { data: [...] }
    return response.data || [];
  },
  
  show: async (id) => {
    const response = await api.get(`/investments/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/investments', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/investments/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/investments/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/investments/${id}/approve`);
    return response.data;
  },
  
  withdraw: async (id) => {
    const response = await api.post(`/investments/${id}/withdraw`);
    return response.data;
  },
};

export const commissionService = {
  index: async () => {
    const response = await api.get('/commissions');
    return response.data.data || response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/commissions/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/commissions', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/commissions/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/commissions/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/commissions/${id}/approve`);
    return response.data;
  },
  
  reject: async (id) => {
    const response = await api.post(`/commissions/${id}/reject`);
    return response.data;
  },
  
  pay: async (id) => {
    const response = await api.post(`/commissions/${id}/pay`);
    return response.data;
  },
};

export const blogService = {
  index: async (params = {}) => {
    const response = await api.get('/blogs', { params });
    return response.data.data || response.data;
  },
  
  show: async (slug) => {
    const response = await api.get(`/blogs/${slug}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/blogs', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/blogs/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/blogs/${id}`);
    return response.data;
  },
};

export const jobService = {
  index: async (params = {}) => {
    const response = await api.get('/jobs', { params });
    return response.data.data || response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/jobs/${id}`);
    return response.data.data || response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/jobs', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/jobs/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },
  
  applications: async (id) => {
    const response = await api.get(`/jobs/${id}/applications`);
    return response.data;
  },

  allApplications: async (params = {}) => {
    const response = await api.get('/applications', { params });
    return response.data;
  },
  
  apply: async (id, data) => {
    // Check if data contains a file (FormData)
    if (data instanceof FormData) {
      const response = await api.post(`/jobs/${id}/apply`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await api.post(`/jobs/${id}/apply`, data);
    return response.data;
  },
};

export const reportService = {
  dashboard: async () => {
    const response = await api.get('/reports/dashboard');
    return response.data;
  },
  
  sales: async (params = {}) => {
    const response = await api.get('/reports/sales', { params });
    return response.data;
  },
  
  topProducts: async (params = {}) => {
    const response = await api.get('/reports/top-products', { params });
    return response.data;
  },
  
  inventory: async () => {
    const response = await api.get('/reports/inventory');
    return response.data;
  },
  
  customers: async () => {
    const response = await api.get('/reports/customers');
    return response.data;
  },
};

export const newsletterService = {
  subscribe: async (data) => {
    const response = await api.post('/newsletter/subscribe', data);
    return response.data;
  },
  index: async (params = {}) => {
    const response = await api.get('/newsletters', { params });
    return response.data.data || response.data;
  },
  store: async (data) => {
    const response = await api.post('/newsletters', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/newsletters/${id}`, data);
    return response.data;
  },
  send: async (id) => {
    const response = await api.post(`/newsletters/${id}/send`);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/newsletters/${id}`);
    return response.data;
  },
  subscribers: async (page = 1) => {
    const response = await api.get('/newsletters/subscribers', { params: { page } });
    return response.data;
  },
  deleteSubscriber: async (id) => {
    const response = await api.delete(`/newsletters/subscribers/${id}`);
    return response.data;
  },
};

export const notificationService = {
  index: async () => {
    const response = await api.get('/notifications');
    return response.data.data || response.data;
  },
  store: async (data) => {
    const response = await api.post('/notifications', data);
    return response.data;
  },
  markAsRead: async (id) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
};

export const adService = {
  index: async (params = {}) => {
    const response = await api.get('/ads', { params });
    return response.data.data || response.data;
  },
  dashboard: async () => {
    const response = await api.get('/ads/dashboard');
    return response.data;
  },
  packages: async () => {
    const response = await api.get('/ads/packages');
    return response.data;
  },
  storePackage: async (data) => {
    const response = await api.post('/ads/packages', data);
    return response.data;
  },
  approve: async (id) => {
    const response = await api.post(`/ads/${id}/approve`);
    return response.data;
  },
  reject: async (id) => {
    const response = await api.post(`/ads/${id}/reject`);
    return response.data;
  },
  store: async (data) => {
    const response = await api.post('/ads', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/ads/${id}`, data);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/ads/${id}`);
    return response.data;
  },
  trackClick: async (id) => {
    const response = await api.post(`/ads/${id}/click`);
    return response.data;
  },
  trackImpression: async (id) => {
    const response = await api.post(`/ads/${id}/impression`);
    return response.data;
  },
};

export const socialMediaService = {
  index: async () => {
    const response = await api.get('/public/social-media');
    return response.data;
  },
  update: async (data) => {
    const response = await api.post('/social-media-links', data);
    return response.data;
  },
  indexPosts: async () => {
    const response = await api.get('/social-posts');
    return response.data;
  },
  storePost: async (data) => {
    const response = await api.post('/social-posts', data);
    return response.data;
  },
  indexCampaigns: async () => {
    const response = await api.get('/campaigns');
    return response.data;
  },
  storeCampaign: async (data) => {
    const response = await api.post('/campaigns', data);
    return response.data;
  },
  showCampaign: async (id) => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },
  updateCampaign: async (id, data) => {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data;
  },
  destroyCampaign: async (id) => {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },
};

export const spotlightService = {
  index: async (params) => {
    const response = await api.get('/spotlights', { params });
    return response.data;
  },
  store: async (data) => {
    const response = await api.post('/spotlights', data);
    return response.data;
  },
  show: async (id) => {
    const response = await api.get(`/spotlights/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/spotlights/${id}`, data);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/spotlights/${id}`);
    return response.data;
  },
};

export const promotionService = {
  index: async (params) => {
    const response = await api.get('/promotions', { params });
    return response.data;
  },
  store: async (data) => {
    const response = await api.post('/promotions', data);
    return response.data;
  },
  show: async (id) => {
    const response = await api.get(`/promotions/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/promotions/${id}`, data);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/promotions/${id}`);
    return response.data;
  },
};

export const siteConfigService = {
  settings: async () => {
    const response = await api.get('/public/settings');
    return response.data;
  },
  sliders: async (position = 'home') => {
    const response = await api.get('/public/sliders', { params: { position } });
    return response.data.data || response.data;
  },
  menu: async (location = 'header') => {
    const response = await api.get('/public/menu', { params: { location } });
    return response.data.data || response.data;
  },
  sections: async (page = 'home') => {
    const response = await api.get('/public/sections', { params: { page } });
    return response.data.data || response.data;
  },
  footer: async () => {
    const response = await api.get('/public/footer');
    return response.data.data || response.data;
  },
};

// Admin dynamic config services
export const siteSettingService = {
  index: async () => { const r = await api.get('/site-settings'); return r.data.data || r.data; },
  store: async (data) => { const r = await api.post('/site-settings', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/site-settings/${id}`, data); return r.data; },
  destroy: async (id) => { const r = await api.delete(`/site-settings/${id}`); return r.data; },
  bulkUpdate: async (settingsArray) => { const r = await api.post('/site-settings/bulk', { settings: settingsArray }); return r.data; },
};

export const sliderService = {
  index: async () => { const r = await api.get('/sliders'); return r.data.data || r.data; },
  store: async (data) => { const r = await api.post('/sliders', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/sliders/${id}`, data); return r.data; },
  destroy: async (id) => { const r = await api.delete(`/sliders/${id}`); return r.data; },
};

export const menuItemService = {
  index: async () => { const r = await api.get('/menu-items'); return r.data.data || r.data; },
  store: async (data) => { const r = await api.post('/menu-items', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/menu-items/${id}`, data); return r.data; },
  destroy: async (id) => { const r = await api.delete(`/menu-items/${id}`); return r.data; },
};

export const pageSectionService = {
  index: async () => { const r = await api.get('/page-sections'); return r.data.data || r.data; },
  store: async (data) => { const r = await api.post('/page-sections', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/page-sections/${id}`, data); return r.data; },
  publicSections: async (pageSlug) => { const r = await api.get('/public/sections', { params: { page: pageSlug } }); return r.data.data || r.data; },
  destroy: async (id) => { const r = await api.delete(`/page-sections/${id}`); return r.data; },
};

export const footerLinkService = {
  index: async () => { const r = await api.get('/footer-links'); return r.data.data || r.data; },
  store: async (data) => { const r = await api.post('/footer-links', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/footer-links/${id}`, data); return r.data; },
  destroy: async (id) => { const r = await api.delete(`/footer-links/${id}`); return r.data; },
};

export const userService = {
  index: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data.data || response.data;
  },
  show: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data.data || response.data;
  },
  store: async (data) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
  roles: async () => {
    const response = await api.get('/users/roles');
    return response.data;
  },
};

export const mediaService = {
  upload: async (file, folder = 'site') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await api.post('/media/upload', formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },
  index: async () => {
    const response = await api.get('/media');
    return response.data;
  },
  destroy: async (path) => {
    const response = await api.delete('/media', { data: { path } });
    return response.data;
  },
};

export const supportTicketService = {
  index: async (params = {}) => {
    const response = await api.get('/support-tickets', { params });
    return response.data.data || response.data;
  },
  store: async (data) => {
    const response = await api.post('/support-tickets', data);
    return response.data;
  },
  show: async (id) => {
    const response = await api.get(`/support-tickets/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/support-tickets/${id}`, data);
    return response.data;
  },
  close: async (id) => {
    const response = await api.post(`/support-tickets/${id}/close`);
    return response.data;
  },
};

export const contactService = {
  submit: async (data) => {
    const response = await api.post('/contact/submit', data);
    return response.data;
  },
  index: async () => {
    const response = await api.get('/contact-submissions');
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/contact-submissions/${id}`);
    return response.data;
  },
};

export const channelRegistrationService = {
  getForm: async () => {
    const response = await api.get('/channel-registration/form');
    return response.data;
  },
  submit: async (data) => {
    const response = await api.post('/channel-registration/submit', data);
    return response.data;
  },
  index: async (params = {}) => {
    const response = await api.get('/channel-registrations', { params });
    return response.data;
  },
  show: async (id) => {
    const response = await api.get(`/channel-registrations/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/channel-registrations/${id}`, data);
    return response.data;
  },
  approve: async (id) => {
    const response = await api.post(`/channel-registrations/${id}/approve`);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/channel-registrations/${id}`);
    return response.data;
  },
  getConfig: async () => {
    const response = await api.get('/channel-registration/config');
    return response.data;
  },
  updateConfig: async (data) => {
    const response = await api.put('/channel-registration/config', data);
    return response.data;
  },
};

export default api;
